import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import type { DistrictMode, MasterCommon } from '@citizen-walk/shared-types';
import { useState, type ReactNode } from 'react';
import { archiveMaster, upsertMaster, type MastersState } from './useMasters';
import { MasterHistoryView } from './MasterHistoryView';
import { MasterImportExportButtons, RowExportButton } from './MasterImportExportButtons';
import { stripSystemFields } from './jsonImportExport';

/**
 * 「mode + 1 つの unknown フィールド (JSON 文字列で編集)」型の master 用 page。
 *
 * 対応 master (Phase 3 sprint 3/5 minimum 段階で詳細 schema が unknown のもの):
 *   - M3 aiOutputFormats: schema
 *   - M5 analysisDisplayConfigs: displayRules
 *   - M8 surveyPresets: questions
 *   - M9 verificationTemplatePresets: template
 *
 * Phase 5 で詳細 sub-interface が具体化されたら個別 page に分岐 (本コンポーネントは廃止 or 縮退)。
 */

interface FormDraft {
  id: string;
  displayName: string;
  mode: DistrictMode;
  jsonText: string;
}

const EMPTY_DRAFT: FormDraft = {
  id: '',
  displayName: '',
  mode: 'ecw',
  jsonText: '',
};

export interface SimpleJsonMasterPageProps<T extends MasterCommon & { mode: DistrictMode }> {
  /** Firestore top-level collection 名 (e.g. 'aiOutputFormats')。 */
  collectionName: string;
  /** ページタイトル (e.g. 'M3 aiOutputFormats')。 */
  title: string;
  /** サブタイトル (docs/04 参照箇所 等の説明)。 */
  subtitle: string;
  /** JSON 編集対象のフィールド名 (T のキー、e.g. 'schema' / 'displayRules' / 'template')。 */
  jsonFieldName: keyof T & string;
  /** JSON フィールドのラベル (UI 表示用)。 */
  jsonFieldLabel: string;
  /** JSON フィールドの placeholder (空 master のサンプル等)。 */
  jsonFieldPlaceholder?: string;
  /** master state (typed hook の戻り値)。 */
  state: MastersState<T & { id: string }>;
  /** 新規時 id 入力の placeholder。 */
  newIdPlaceholder: string;
  /**
   * 各 row の操作 buttons 群の前に表示する追加 action (M9 visual editor link 等)。
   * Sprint BG-DataSettings 13 — M9 verificationTemplatePresets の visual editor 個別 page
   * への遷移 link を追加するため (GAP A-S24/S25)。
   */
  extraRowAction?: (item: T & { id: string }) => ReactNode;
}

export function SimpleJsonMasterPage<T extends MasterCommon & { mode: DistrictMode }>(
  props: SimpleJsonMasterPageProps<T>,
) {
  const { toast } = useToast();
  const [editing, setEditing] = useState<FormDraft | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [historyTarget, setHistoryTarget] = useState<{ id: string; name: string } | null>(null);

  function handleNew() {
    setEditing({ ...EMPTY_DRAFT });
    setIsNew(true);
  }

  function handleEdit(item: T & { id: string }) {
    const currentJson = item[props.jsonFieldName as keyof typeof item] ?? null;
    setEditing({
      id: item.id,
      displayName: item.displayName,
      mode: item.mode,
      jsonText: currentJson === null ? '' : JSON.stringify(currentJson, null, 2),
    });
    setIsNew(false);
  }

  async function handleSave() {
    if (!editing) return;
    if (!editing.displayName.trim()) {
      toast('error', 'displayName は必須です');
      return;
    }
    if (isNew && !editing.id.trim()) {
      toast('error', '新規時は id (kebab-case) を指定してください');
      return;
    }
    let parsedJson: unknown = null;
    if (editing.jsonText.trim()) {
      try {
        parsedJson = JSON.parse(editing.jsonText);
      } catch (e) {
        toast('error', `${props.jsonFieldLabel} の JSON が不正: ${(e as Error).message}`);
        return;
      }
    }
    setSaving(true);
    try {
      // 動的フィールド構成 (T の jsonFieldName を含む)
      const data = {
        mode: editing.mode,
        displayName: editing.displayName.trim(),
        [props.jsonFieldName]: parsedJson,
        enabled: true,
        version: 1,
        schemaVersion: 1,
        archivedAt: null,
      } as unknown as Omit<T, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'>;
      await upsertMaster<T>({
        collectionName: props.collectionName,
        id: editing.id || undefined,
        uid: 'admin-stub', // Phase 5 で実 master role uid に置換
        isNew,
        data,
      });
      toast('success', isNew ? '新規作成しました' : '更新しました');
      setEditing(null);
      setIsNew(false);
    } catch (e) {
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  async function handleArchive(id: string, name: string) {
    if (!confirm(`「${name}」をアーカイブしますか? (物理削除ではなく archivedAt 設定)`)) return;
    try {
      await archiveMaster(props.collectionName, id, 'admin-stub'); // Phase 5 で実 master role uid に置換
      toast('success', 'アーカイブしました');
    } catch (e) {
      toast('error', `アーカイブ失敗: ${(e as Error).message}`);
    }
  }

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">{props.title}</h1>
        <p className="text-sm text-slate-500 mt-1">{props.subtitle}</p>
      </header>

      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <Button variant="primary" size="md" onClick={handleNew} disabled={editing !== null}>
          ＋ 新規作成
        </Button>
        <MasterImportExportButtons<T & { id: string }>
          collectionName={props.collectionName}
          items={props.state.status === 'ready' ? props.state.items : []}
          validator={(raw) => {
            if (!raw || typeof raw !== 'object') return null;
            const r = raw as Record<string, unknown>;
            if (typeof r.id !== 'string' || typeof r.displayName !== 'string') return null;
            if (typeof r.mode !== 'string') return null;
            return r as unknown as T & { id: string };
          }}
          onImport={async (importedItems) => {
            for (const it of importedItems) {
              // Sprint BG-MasterIO follow-up — system field を除去
              const stripped = stripSystemFields(it as unknown as Record<string, unknown>);
              const { id, ...rest } = stripped as { id: string } & Record<string, unknown>;
              // existing 判定: state に同 id があれば update、無ければ create
              const existing =
                props.state.status === 'ready'
                  ? props.state.items.find((x) => x.id === id)
                  : undefined;
              await upsertMaster<T>({
                collectionName: props.collectionName,
                id,
                uid: 'admin-stub',
                isNew: !existing,
                data: rest as unknown as Omit<
                  T,
                  'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy'
                >,
              });
            }
          }}
          disabled={editing !== null}
          expectedMode={['ecw', 'bousai', 'walker', 'kids-bousai']}
        />
      </div>

      {props.state.status === 'loading' && <p className="text-slate-500">読込中…</p>}
      {props.state.status === 'error' && (
        <p className="text-danger-600">読込エラー: {props.state.error.message}</p>
      )}
      {props.state.status === 'ready' && (
        <>
          <p className="text-xs text-slate-500 mb-3">
            件数: {props.state.items.length} (アーカイブ済除外)
          </p>
          <ul className="grid grid-cols-1 gap-2 mb-6">
            {props.state.items.map((item) => (
              <li key={item.id}>
                <Card padding="md" className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <strong className="text-slate-900">{item.displayName}</strong>
                    <span className="ml-2 text-xs text-slate-500">id: {item.id}</span>
                    <span className="ml-2 text-xs text-slate-500">mode: {item.mode}</span>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {props.extraRowAction?.(item)}
                    <RowExportButton collectionName={props.collectionName} item={item} />
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleEdit(item)}
                      aria-label={`${item.displayName} を編集`}
                    >
                      編集
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setHistoryTarget({ id: item.id, name: item.displayName })}
                      aria-label={`${item.displayName} の履歴を表示`}
                    >
                      履歴
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => void handleArchive(item.id, item.displayName)}
                      aria-label={`${item.displayName} をアーカイブ`}
                    >
                      アーカイブ
                    </Button>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        </>
      )}

      {historyTarget && (
        <MasterHistoryView
          collectionName={props.collectionName}
          masterId={historyTarget.id}
          masterDisplayName={historyTarget.name}
          onClose={() => setHistoryTarget(null)}
        />
      )}

      {editing && (
        <Card padding="lg" className="border-primary-300">
          <h2 className="text-lg font-bold mb-3">{isNew ? '新規作成' : '編集'}</h2>
          <div className="grid grid-cols-1 gap-3">
            {isNew && (
              <div>
                <label className="block text-xs text-slate-600 mb-1" htmlFor="sjm-id">
                  id (kebab-case、保存後変更不可) <span className="text-danger-600">*</span>
                </label>
                <input
                  id="sjm-id"
                  type="text"
                  value={editing.id}
                  onChange={(e) => setEditing({ ...editing, id: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400"
                  placeholder={props.newIdPlaceholder}
                />
              </div>
            )}
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="sjm-name">
                displayName <span className="text-danger-600">*</span>
              </label>
              <input
                id="sjm-name"
                type="text"
                value={editing.displayName}
                onChange={(e) => setEditing({ ...editing, displayName: e.target.value })}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="sjm-mode">
                mode
              </label>
              <select
                id="sjm-mode"
                value={editing.mode}
                onChange={(e) => setEditing({ ...editing, mode: e.target.value as DistrictMode })}
                className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400"
              >
                <option value="ecw">ecw</option>
                <option value="bousai">bousai</option>
                <option value="walker">walker</option>
                <option value="kids-bousai">kids-bousai</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-600 mb-1" htmlFor="sjm-json">
                {props.jsonFieldLabel} (JSON)
              </label>
              <textarea
                id="sjm-json"
                value={editing.jsonText}
                onChange={(e) => setEditing({ ...editing, jsonText: e.target.value })}
                rows={10}
                className="w-full px-3 py-2 text-xs font-mono border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-400 resize-y"
                placeholder={props.jsonFieldPlaceholder ?? '{ }'}
                spellCheck={false}
              />
              <p className="text-xs text-slate-500 mt-1">
                空白可。Phase 5 で詳細 sub-form に置換予定 (現状は JSON で柔軟性確保)。
              </p>
            </div>
            <div className="flex justify-end gap-2 mt-2">
              <Button
                variant="secondary"
                size="md"
                onClick={() => {
                  setEditing(null);
                  setIsNew(false);
                }}
                disabled={saving}
              >
                キャンセル
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={() => void handleSave()}
                disabled={saving}
              >
                {saving ? '保存中...' : '保存'}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
